import { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Outlet, ScrollRestoration, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useAuth } from '@/features/auth/auth-context';
import { AdminSidebar } from './AdminSidebar';
import { DRAWER_WIDTH } from './admin-nav';

export function AdminLayout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { logout } = useAuth();
  const { t } = useTranslation('admin');
  const navigate = useNavigate();

  const signOut = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.default' }}>
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isDesktop ? 'permanent' : 'temporary'}
          open={isDesktop || mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderRight: 1,
              borderColor: 'divider',
            },
          }}
        >
          <AdminSidebar onNavigate={() => setMobileOpen(false)} />
        </Drawer>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AppBar position="sticky" sx={{ bgcolor: 'background.default' }}>
          <Toolbar sx={{ gap: 1 }}>
            <IconButton
              onClick={() => setMobileOpen(true)}
              sx={{ display: { md: 'none' } }}
              aria-label="Menu"
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title={t('nav.viewSite')}>
                <IconButton href="/" target="_blank" size="small" aria-label={t('nav.viewSite')}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <LanguageSwitcher />
              <ThemeToggle />
              <Button
                onClick={() => void signOut()}
                startIcon={<LogoutIcon />}
                color="inherit"
                size="small"
                sx={{ ml: 0.5 }}
              >
                {t('nav.logout')}
              </Button>
            </Stack>
          </Toolbar>
        </AppBar>

        <Box component="main" sx={{ flex: 1, p: { xs: 2.5, md: 4 } }}>
          <Outlet />
        </Box>
      </Box>
      <ScrollRestoration />
    </Box>
  );
}
